'use client';

/**
 * The header card above a DataTable: a title, the page's own controls, the
 * search box with its magnifier, and the column manager.
 *
 * Ported from glp-frontend `components/helpers/data-table/toolbar.tsx`.
 */
import type { ReactNode } from 'react';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { Search } from 'lucide-react';

import { cn } from '../../lib/utils';
import { Input } from '../ui/input';
import { ColumnManager } from './column-manager';

export const TOOLBAR_SEARCH_CLASS =
  // Important modifiers on width and left padding: legacy page stylesheets
  // restyle every input under their root id.
  'h-10 !w-full rounded-[19px] border-2 border-[rgba(193,199,214,0.35)] bg-white !pl-10 text-sm text-gray-700 placeholder:text-gray-400 focus:border-global-green focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0';

export interface DataTableToolbarProps<TData> {
  title?: ReactNode;
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    /** Lets a test or a label find the box. */
    id?: string;
  };
  /** With the three below, renders the column manager. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns?: ColumnDef<TData, any>[];
  columnVisibility?: VisibilityState;
  onToggleColumn?: (key: string) => void;
  onShowAllColumns?: () => void;
  /** A page's own controls: filter selects, action buttons. */
  children?: ReactNode;
  /** No card chrome - for a toolbar that already sits inside a card. */
  plain?: boolean;
  className?: string;
}

export function DataTableToolbar<TData>({
  title,
  search,
  columns,
  columnVisibility,
  onToggleColumn,
  onShowAllColumns,
  children,
  plain = false,
  className,
}: DataTableToolbarProps<TData>) {
  const showManager = Boolean(columns && columnVisibility && onToggleColumn && onShowAllColumns);
  // Page controls + search + manager: on phones the search gets its own row.
  const splitRow = Boolean(children && search && showManager);

  return (
    <div
      className={cn(
        'mb-4 flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        !plain && 'rounded-[19px] bg-white px-4 py-6 shadow-sm',
        className,
      )}
    >
      {title && <h2 className='text-lg font-semibold text-[#232323]'>{title}</h2>}

      <div
        className={cn(
          'flex w-full min-w-0 gap-3 sm:w-auto sm:flex-1 sm:flex-row sm:flex-nowrap sm:items-center sm:justify-end',
          splitRow ? 'flex-row flex-wrap items-center' : 'flex-col',
        )}
      >
        {/* With page controls, a search and the column manager, a phone shows
            the controls with the manager beside them and the search on its own
            full-width row below, so long placeholders fit. The wrappers are
            display:contents from sm, so desktop stays one row: controls,
            search, manager. */}
        {splitRow ? (
          <div data-toolbar-controls='' className='min-w-0 flex-1 sm:contents'>
            {children}
          </div>
        ) : (
          children
        )}
        {/* Otherwise the search and the manager share one row: on a phone the
            search takes the leftover width and the manager sits beside it.
            From sm the search grows with the toolbar (min 16rem, max 20rem)
            instead of a fixed width, so long placeholders are not cut off. */}
        {(search || showManager) && (
          <div
            data-toolbar-search-row=''
            className={cn(
              'min-w-0 items-center justify-end gap-3 sm:flex sm:w-auto sm:flex-1',
              splitRow ? 'contents' : 'flex w-full',
            )}
          >
            {search && (
              <div
                className={cn(
                  'relative min-w-0 flex-1 sm:min-w-[16rem] sm:max-w-xs',
                  splitRow && 'order-last basis-full sm:order-none sm:basis-auto',
                )}
              >
                <Input
                  id={search.id}
                  type='text'
                  placeholder={search.placeholder ?? 'Search ...'}
                  value={search.value}
                  onChange={(e) => search.onChange(e.target.value)}
                  className={TOOLBAR_SEARCH_CLASS}
                />
                <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
              </div>
            )}
            {showManager && (
              <ColumnManager
                className='ml-auto'
                columns={columns!}
                columnVisibility={columnVisibility!}
                onToggle={onToggleColumn!}
                onShowAll={onShowAllColumns!}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DataTableToolbar;
